import { useAtom } from "jotai";

import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@frontend/components/atoms/Select";
import { ContactAddressPlaceLabels } from "@frontend/features/addressbook";
import type { InferFieldsFromSchema } from "@frontend/lib/form";
import { AddressFields as SharedAddressFields } from "@frontend/shared/address-fields";
import { Trans, useLingui } from "@lingui/react/macro";

import type { newAddressSchema } from "../schemas/location";

export type AddressFieldsProps = {
  fields: InferFieldsFromSchema<typeof newAddressSchema>;
  disabled?: boolean;
  autoPostalCode?: boolean;
  autoPostalCodeToggle?: React.ReactNode;
};

export function AddressFields({
  fields,
  disabled,
  autoPostalCode = true,
  autoPostalCodeToggle,
}: AddressFieldsProps) {
  return (
    <SharedAddressFields
      fields={fields}
      disabled={disabled}
      autoPostalCode={autoPostalCode}
      autoPostalCodeToggle={
        autoPostalCodeToggle ? (
          <Field>
            <FieldContent>{autoPostalCodeToggle}</FieldContent>
          </Field>
        ) : null
      }
      place={<PlaceField field={fields.place} disabled={disabled} />}
    />
  );
}

type PlaceFieldProps = {
  field: InferFieldsFromSchema<typeof newAddressSchema>["place"];
  disabled?: boolean;
};

function PlaceField({ field, disabled }: PlaceFieldProps) {
  const { i18n, t } = useLingui();

  const [place, setPlace] = useAtom(field.atom);

  const items = Object.entries(ContactAddressPlaceLabels).map(
    ([value, label]) => ({ value, label: i18n._(label) }),
  );

  return (
    <Field>
      <FieldLabel>
        <Trans>Place</Trans>
      </FieldLabel>

      <FieldContent>
        <Select
          items={items}
          value={place || null}
          onValueChange={(value) => setPlace(value ?? "")}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue placeholder={t`Select a place`} />
          </SelectTrigger>

          <SelectContent>
            {items.map((item) => (
              <SelectItem key={item.value} value={item.value}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FieldContent>
    </Field>
  );
}
